import { FC, Fragment, useCallback } from "react";
import Typography from "@mui/material/Typography";
import { Box, IconButton, Stack, Tooltip } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { useSearchParams } from "react-router-dom";
import { useDidMount } from "rooks";
import { ResourceExplorerBudgetProgress } from "./ResourceExplorerBudgetProgress";
import { ResourceExplorerBudgetTooltip } from "./tooltip/ResourceExplorerBudgetTooltip";
import { CreateBudgetButtonComponent } from "./CreateBudgetButtonComponent";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { money } from "@/utils/numeral/money";
import { useAppAbility } from "@/services/permissions";
import { useHover } from "@/utils/hooks/useHover.hook";
import { resourceExplorerShowBudgetLineSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/data/resourceExplorerShowBudgetLineSelector";
import { toggleShowBudgetLine } from "@/store/resource-explorer/resourceExplorerSlice";
import { resourceExplorerChartBaseSelector } from "@/store/resource-explorer/selectors/resourceExplorerChartBaseSelector";
import { metaDataTitleHeight } from "@/components/pages/common/info-cards/InfoCard";
import { Budget } from "@/services/cloudchipr.api";
import { resourceExplorerViewBudgetsSelector } from "@/store/resource-explorer/selectors/budget/resourceExplorerViewBudgetsSelector";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import { getPlannedBudgetAmount } from "@/components/pages/budgets-and-alerts/components/grids/budgets/utils/helpers/getPlannedBudgetAmount";
import { updateBillingExplorerChartSettingsThunk } from "@/store/resource-explorer/thunks/resource-explorer/updateBillingExplorerChartSettingsThunk";
import { organisationQuarterStartMonthSelector } from "@/store/common/selectors/org-current-settings/organisationQuarterStartMonthSelector";
import { calculateBudgetProgress } from "@/components/pages/budgets-and-alerts/utils/helpers/calculateBudgetProgress";
import { ResourceExplorerBudgetSelect } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/resource-explorer-metadata/components/budgets/budget-select/ResourceExplorerBudgetSelect";
import { isBudgetUsedDataNoCollected } from "@/components/pages/budgets-and-alerts/components/grids/budgets/utils/helpers/isBudgetUsedDataNoCollected";

export const ResourceExplorerBudget: FC<{
  budget: Budget;
  withPopover?: boolean;
}> = ({ budget, withPopover }) => {
  const { can } = useAppAbility();
  const dispatch = useAppDispatch();
  const { ref, hovered } = useHover();
  const showBudgetLine = useAppSelector(resourceExplorerShowBudgetLineSelector);
  const chartBase = useAppSelector(resourceExplorerChartBaseSelector);
  const canEditBudget = can("edit", "budget");

  const budgets = useAppSelector(resourceExplorerViewBudgetsSelector);
  const noData = isBudgetUsedDataNoCollected(
    budget?.used ?? 0,
    budget?.start_date,
    budget?.period,
  );

  const quarterStartMonth = useAppSelector(
    organisationQuarterStartMonthSelector,
  );

  const [searchParams] = useSearchParams();
  const budgetId = searchParams.get("budgetId");

  const tooltipText = showBudgetLine ? "Hide Budget Line" : "Show Budget Line";

  const handleShowBudgetLineClick = useCallback(() => {
    dispatch(toggleShowBudgetLine());
  }, [dispatch]);

  useDidMount(() => {
    if (budgets && budgetId) {
      dispatch(
        updateBillingExplorerChartSettingsThunk({
          selected_budget_id: budgetId,
        }),
      );
    }
  });

  if (!budget) {
    return null;
  }

  const budgetAmount =
    budget?.type === "fixed"
      ? budget?.amount
      : getPlannedBudgetAmount(
          budget.start_date,
          budget.period,
          budget.planned_amounts,
          quarterStartMonth,
        );

  const progress = calculateBudgetProgress(budget?.used, budgetAmount);

  return (
    <Box ref={ref} py={1}>
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        height={metaDataTitleHeight}
        maxWidth={300}
      >
        {canEditBudget ? (
          <CreateBudgetButtonComponent />
        ) : (
          <TypographyWithTooltip
            title="Budget"
            variant="subtitle2"
            color="text.secondary"
            fontWeight="regular"
          />
        )}

        <Tooltip
          title={
            chartBase === "percent"
              ? "Budget line is hidden in percentage (%) mode. Switch to dollar ($) mode to see it."
              : tooltipText
          }
          arrow
          placement="top"
        >
          <span>
            <IconButton
              onClick={handleShowBudgetLineClick}
              size="small"
              disabled={chartBase === "percent"}
              sx={{ visibility: hovered ? "visible" : "hidden" }}
            >
              {!showBudgetLine ? (
                <VisibilityOffOutlinedIcon fontSize="inherit" />
              ) : (
                <VisibilityOutlinedIcon fontSize="inherit" />
              )}
            </IconButton>
          </span>
        </Tooltip>

        {withPopover && (
          <ResourceExplorerBudgetSelect selectedBudgetId={budget.id} />
        )}
      </Stack>

      <ResourceExplorerBudgetTooltip
        name={budget.name}
        amount={budgetAmount ?? 0}
        used={budget.used}
        progress={progress}
        startDate={budget.start_date}
        period={budget.period}
        hideBudgetDateLabel
      >
        <Fragment>
          <Typography variant="h6" fontWeight="medium">
            {money(budgetAmount)}
          </Typography>
          <ResourceExplorerBudgetProgress
            noData={noData}
            progress={progress}
            startDate={budget.start_date}
          />
        </Fragment>
      </ResourceExplorerBudgetTooltip>
    </Box>
  );
};
